import { type MouseEvent, useEffect, useState } from 'react';
import '../../../../../css/flow/shared-elements.less';
import ButtonDefault from '../../../components/buttons/ButtonDefault';
import Modal from '../../../components/generic/modal/GenericModal';
import Loader from '../../../components/loader/Loader';
import {
    FRIENDLY_DEPENDENCY_NAMES,
    type FriendlyDependencyName,
} from '../../../constants/dependency';
import { SYSTEM_TYPE_IDS, SYSTEM_VALUE_IDS } from '../../../constants/system';
import { addElement, getFlowSharedElements, removeElement } from '../../../sources/flow';
import { getAllMacros } from '../../../sources/macro';
import { getAllPages } from '../../../sources/page';
import { getAllServices } from '../../../sources/service';
import { getAllTypes } from '../../../sources/type';
import { getAllValues } from '../../../sources/value';
import type { ElementAPI } from '../../../types/element';
import { isNullOrEmpty } from '../../../utils/guard';
import { useNotificationDispatch } from '../../notifications/NotificationProvider';
import ImportedElements from './ImportedElements';
import SharedElementsTable from './SharedElementsTable';
import SharedElementTab from './SharedElementTab';
import TabContentControls from './TabContentControls';
import { mapFriendlyNameToDisplay } from './utils';

type Props = {
    flowId: string;
    close: () => void;
    container: HTMLElement | null;
};

const SharedElementsModal = ({ flowId, close, container }: Props) => {
    const [activeTab, setActiveTab] = useState<FriendlyDependencyName>(
        FRIENDLY_DEPENDENCY_NAMES.VALUE_ELEMENT,
    );
    const [showingImportPage, setShowingImportPage] = useState(false);
    const [tableData, setTableData] = useState<ElementAPI[]>([]);
    const [filteredTableData, setFilteredTableData] = useState<ElementAPI[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchText, setSearchText] = useState('');

    const dispatchNotification = useNotificationDispatch();

    const SYSTEM_VALUE_AND_TYPE_IDS = SYSTEM_VALUE_IDS.concat(SYSTEM_TYPE_IDS);

    // biome-ignore lint/correctness/useExhaustiveDependencies: Requires dedicated refactor to include all dependencies
    useEffect(() => {
        loadMainPage();
    }, [activeTab]);

    const getElementsInFlow = async () => {
        try {
            const response = await getFlowSharedElements(flowId, activeTab);

            const filteredResponse = filterOutSystemElements(response);
            setTableData(filteredResponse);
            setFilteredTableData(filteredResponse);
            setIsLoading(false);
        } catch (error) {
            dispatchNotification({
                type: 'notifyError',
                payload: { error },
            });
        }
    };

    const importElementIntoFlow = (id: string) => {
        addElement(id, flowId, activeTab)
            .then(loadMainPage)
            .catch((error) =>
                dispatchNotification({
                    type: 'notifyError',
                    payload: { error },
                }),
            );
    };

    const filterOutSystemElements = (newData: ElementAPI[]) => {
        return newData.filter((item) => !SYSTEM_VALUE_AND_TYPE_IDS.includes(item.id));
    };

    const removeElementFromFlow = (id: string) => {
        removeElement(id, flowId, activeTab)
            .then(loadMainPage)
            .catch((error) =>
                dispatchNotification({
                    type: 'notifyError',
                    payload: { error },
                }),
            );
    };

    const loadImportPage = () => {
        setShowingImportPage(true);
    };

    const loadMainPage = () => {
        setIsLoading(true);
        setShowingImportPage(false);
        getElementsInFlow();
    };

    const changeActiveTab = (
        e: MouseEvent<HTMLButtonElement>,
        tabValue: FriendlyDependencyName,
    ) => {
        if (tabValue !== activeTab) {
            e.preventDefault();
            e.stopPropagation();

            setActiveTab(tabValue);
        }
    };

    const filterBySearchTerms = (searchInput: string) => {
        if (isNullOrEmpty(searchInput) && tableData.length !== filteredTableData.length) {
            setFilteredTableData(tableData);
        } else {
            const filteredTableData = tableData.filter((row) => {
                const name = row.developerName ? row.developerName.toLowerCase() : '';
                return name.includes(searchInput.toLowerCase());
            });

            setFilteredTableData(filteredTableData);
        }
    };

    const renderLoadingSection = <Loader message="Loading data..." />;

    const importPageBody = () => (
        <>
            <ImportedElements
                elementType={activeTab}
                importElement={importElementIntoFlow}
                isOpen={activeTab === FRIENDLY_DEPENDENCY_NAMES.VALUE_ELEMENT}
                onFetch={getAllValues}
            />
            <ImportedElements
                elementType={activeTab}
                importElement={importElementIntoFlow}
                isOpen={activeTab === FRIENDLY_DEPENDENCY_NAMES.TYPE_ELEMENT}
                onFetch={getAllTypes}
            />
            <ImportedElements
                elementType={activeTab}
                importElement={importElementIntoFlow}
                isOpen={activeTab === FRIENDLY_DEPENDENCY_NAMES.PAGE}
                onFetch={getAllPages}
            />
            <ImportedElements
                elementType={activeTab}
                importElement={importElementIntoFlow}
                isOpen={activeTab === FRIENDLY_DEPENDENCY_NAMES.MACRO_ELEMENT}
                onFetch={getAllMacros}
            />
            <ImportedElements
                elementType={activeTab}
                importElement={importElementIntoFlow}
                isOpen={activeTab === FRIENDLY_DEPENDENCY_NAMES.SERVICE_ELEMENT}
                onFetch={getAllServices}
            />
        </>
    );

    const importPageContent = (
        <Modal
            renderHeader={() => (
                <h4 className="modal-title" data-testid="import-header">
                    Add {mapFriendlyNameToDisplay(activeTab)} To Flow
                </h4>
            )}
            renderBody={importPageBody}
            renderFooter={() => (
                <ButtonDefault title="Close Modal" onClick={loadMainPage}>
                    Back
                </ButtonDefault>
            )}
            container={container}
            onHide={close}
        />
    );

    const tabContent = () => {
        return isLoading ? (
            renderLoadingSection
        ) : (
            <>
                <TabContentControls
                    searchText={searchText}
                    setSearchText={setSearchText}
                    filterBySearchTerms={filterBySearchTerms}
                    loadImportPage={loadImportPage}
                    loadMainPage={loadMainPage}
                />
                <SharedElementsTable
                    tableData={filteredTableData}
                    removeElement={removeElementFromFlow}
                />
            </>
        );
    };

    const body = () => {
        return (
            <>
                <ul className="nav nav-tabs">
                    <SharedElementTab
                        activeTab={activeTab}
                        dependencyName={FRIENDLY_DEPENDENCY_NAMES.VALUE_ELEMENT}
                        changeActiveTab={changeActiveTab}
                    />
                    <SharedElementTab
                        activeTab={activeTab}
                        dependencyName={FRIENDLY_DEPENDENCY_NAMES.PAGE}
                        changeActiveTab={changeActiveTab}
                    />
                    <SharedElementTab
                        activeTab={activeTab}
                        dependencyName={FRIENDLY_DEPENDENCY_NAMES.TYPE_ELEMENT}
                        changeActiveTab={changeActiveTab}
                    />
                    <SharedElementTab
                        activeTab={activeTab}
                        dependencyName={FRIENDLY_DEPENDENCY_NAMES.SERVICE_ELEMENT}
                        changeActiveTab={changeActiveTab}
                    />
                    <SharedElementTab
                        activeTab={activeTab}
                        dependencyName={FRIENDLY_DEPENDENCY_NAMES.MACRO_ELEMENT}
                        changeActiveTab={changeActiveTab}
                    />
                </ul>
                {tabContent()}
            </>
        );
    };

    const mainContent = (
        <Modal
            className="config-modal"
            renderHeader={() => <h4 className="modal-title">Shared Elements</h4>}
            renderBody={body}
            renderFooter={() => (
                <ButtonDefault title="Close Modal" onClick={close}>
                    Close
                </ButtonDefault>
            )}
            container={container}
            onHide={close}
        />
    );

    return showingImportPage ? importPageContent : mainContent;
};

export default SharedElementsModal;
